import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import {
  Package, Beer, Pizza, Coffee, IceCream, Sandwich, Wine, Cigarette,
  Droplets, Zap, Cookie, Soup, Cherry, UtensilsCrossed, Beef, Fish,
  Salad, Cake, Pill, ShoppingBag, Candy, Apple, Milk, Croissant,
  Donut, Popcorn, Martini, GlassWater, CupSoda, Banana, Carrot, Egg,
  Ham, Drumstick, Flame, LucideIcon
} from "lucide-react";

type Category = Tables<"categories">;

// Icon mapping
const ICON_MAP: Record<string, LucideIcon> = {
  "package": Package,
  "beer": Beer,
  "pizza": Pizza,
  "coffee": Coffee,
  "ice-cream": IceCream,
  "sandwich": Sandwich,
  "wine": Wine,
  "cigarette": Cigarette,
  "droplets": Droplets,
  "zap": Zap,
  "cookie": Cookie,
  "soup": Soup,
  "cherry": Cherry,
  "utensils-crossed": UtensilsCrossed,
  "beef": Beef,
  "fish": Fish,
  "salad": Salad,
  "cake": Cake,
  "pill": Pill,
  "shopping-bag": ShoppingBag,
  "candy": Candy,
  "apple": Apple,
  "milk": Milk,
  "croissant": Croissant,
  "donut": Donut,
  "popcorn": Popcorn,
  "martini": Martini,
  "glass-water": GlassWater,
  "cup-soda": CupSoda,
  "banana": Banana,
  "carrot": Carrot,
  "egg": Egg,
  "ham": Ham,
  "drumstick": Drumstick,
  "flame": Flame,
};

// PDV dark-theme color styles for category tabs
const PDV_COLOR_STYLES: Record<string, { unselected: string; selected: string }> = {
  blue: {
    unselected: "bg-[#0a0e1a] border-blue-500/30 text-blue-400 hover:bg-blue-900/20 hover:border-blue-500/50",
    selected: "bg-blue-900/40 border-blue-500/60 text-blue-300 shadow-[0_0_10px_rgba(59,130,246,0.25)]",
  },
  green: {
    unselected: "bg-[#0a0e1a] border-green-500/30 text-green-400 hover:bg-green-900/20 hover:border-green-500/50",
    selected: "bg-green-900/40 border-green-500/60 text-green-300 shadow-[0_0_10px_rgba(34,197,94,0.25)]",
  },
  orange: {
    unselected: "bg-[#0a0e1a] border-orange-500/30 text-orange-400 hover:bg-orange-900/20 hover:border-orange-500/50",
    selected: "bg-orange-900/40 border-orange-500/60 text-orange-300 shadow-[0_0_10px_rgba(249,115,22,0.25)]",
  },
  purple: {
    unselected: "bg-[#0a0e1a] border-purple-500/30 text-purple-400 hover:bg-purple-900/20 hover:border-purple-500/50",
    selected: "bg-purple-900/40 border-purple-500/60 text-purple-300 shadow-[0_0_10px_rgba(168,85,247,0.25)]",
  },
  pink: {
    unselected: "bg-[#0a0e1a] border-pink-500/30 text-pink-400 hover:bg-pink-900/20 hover:border-pink-500/50",
    selected: "bg-pink-900/40 border-pink-500/60 text-pink-300 shadow-[0_0_10px_rgba(236,72,153,0.25)]",
  },
  cyan: {
    unselected: "bg-[#0a0e1a] border-cyan-500/30 text-cyan-400 hover:bg-cyan-900/20 hover:border-cyan-500/50",
    selected: "bg-cyan-900/40 border-cyan-500/60 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.25)]",
  },
  amber: {
    unselected: "bg-[#0a0e1a] border-amber-500/30 text-amber-400 hover:bg-amber-900/20 hover:border-amber-500/50",
    selected: "bg-amber-900/40 border-amber-500/60 text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.25)]",
  },
  red: {
    unselected: "bg-[#0a0e1a] border-red-500/30 text-red-400 hover:bg-red-900/20 hover:border-red-500/50",
    selected: "bg-red-900/40 border-red-500/60 text-red-300 shadow-[0_0_10px_rgba(239,68,68,0.25)]",
  },
  indigo: {
    unselected: "bg-[#0a0e1a] border-indigo-500/30 text-indigo-400 hover:bg-indigo-900/20 hover:border-indigo-500/50",
    selected: "bg-indigo-900/40 border-indigo-500/60 text-indigo-300 shadow-[0_0_10px_rgba(99,102,241,0.25)]",
  },
  teal: {
    unselected: "bg-[#0a0e1a] border-teal-500/30 text-teal-400 hover:bg-teal-900/20 hover:border-teal-500/50",
    selected: "bg-teal-900/40 border-teal-500/60 text-teal-300 shadow-[0_0_10px_rgba(20,184,166,0.25)]",
  },
  lime: {
    unselected: "bg-[#0a0e1a] border-lime-500/30 text-lime-400 hover:bg-lime-900/20 hover:border-lime-500/50",
    selected: "bg-lime-900/40 border-lime-500/60 text-lime-300 shadow-[0_0_10px_rgba(132,204,22,0.25)]",
  },
  rose: {
    unselected: "bg-[#0a0e1a] border-rose-500/30 text-rose-400 hover:bg-rose-900/20 hover:border-rose-500/50",
    selected: "bg-rose-900/40 border-rose-500/60 text-rose-300 shadow-[0_0_10px_rgba(244,63,94,0.25)]",
  },
};

// Legacy color styles (non-PDV contexts)
const COLOR_STYLES: Record<string, { unselected: string; selected: string }> = {
  blue: {
    unselected: "bg-blue-100 border-blue-300 text-blue-700 hover:bg-blue-200",
    selected: "bg-blue-500 text-white border-blue-600 hover:bg-blue-600",
  },
  green: {
    unselected: "bg-green-100 border-green-300 text-green-700 hover:bg-green-200",
    selected: "bg-green-500 text-white border-green-600 hover:bg-green-600",
  },
  orange: {
    unselected: "bg-orange-100 border-orange-300 text-orange-700 hover:bg-orange-200",
    selected: "bg-orange-500 text-white border-orange-600 hover:bg-orange-600",
  },
  purple: {
    unselected: "bg-purple-100 border-purple-300 text-purple-700 hover:bg-purple-200",
    selected: "bg-purple-500 text-white border-purple-600 hover:bg-purple-600",
  },
  pink: {
    unselected: "bg-pink-100 border-pink-300 text-pink-700 hover:bg-pink-200",
    selected: "bg-pink-500 text-white border-pink-600 hover:bg-pink-600",
  },
  cyan: {
    unselected: "bg-cyan-100 border-cyan-300 text-cyan-700 hover:bg-cyan-200",
    selected: "bg-cyan-500 text-white border-cyan-600 hover:bg-cyan-600",
  },
  amber: {
    unselected: "bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200",
    selected: "bg-amber-500 text-white border-amber-600 hover:bg-amber-600",
  },
  red: {
    unselected: "bg-red-100 border-red-300 text-red-700 hover:bg-red-200",
    selected: "bg-red-500 text-white border-red-600 hover:bg-red-600",
  },
  indigo: {
    unselected: "bg-indigo-100 border-indigo-300 text-indigo-700 hover:bg-indigo-200",
    selected: "bg-indigo-500 text-white border-indigo-600 hover:bg-indigo-600",
  },
  teal: {
    unselected: "bg-teal-100 border-teal-300 text-teal-700 hover:bg-teal-200",
    selected: "bg-teal-500 text-white border-teal-600 hover:bg-teal-600",
  },
  lime: {
    unselected: "bg-lime-100 border-lime-300 text-lime-700 hover:bg-lime-200",
    selected: "bg-lime-500 text-white border-lime-600 hover:bg-lime-600",
  },
  rose: {
    unselected: "bg-rose-100 border-rose-300 text-rose-700 hover:bg-rose-200",
    selected: "bg-rose-500 text-white border-rose-600 hover:bg-rose-600",
  },
};

// Border styles for product cards based on category color
export const PRODUCT_BORDER_STYLES: Record<string, string> = {
  blue: "border-blue-400",
  green: "border-green-400",
  orange: "border-orange-400",
  purple: "border-purple-400",
  pink: "border-pink-400",
  cyan: "border-cyan-400",
  amber: "border-amber-400",
  red: "border-red-400",
  indigo: "border-indigo-400",
  teal: "border-teal-400",
  lime: "border-lime-400",
  rose: "border-rose-400",
};

export function getProductBorderColor(colorName: string | null | undefined): string {
  if (!colorName || !PRODUCT_BORDER_STYLES[colorName]) {
    return "border-border";
  }
  return PRODUCT_BORDER_STYLES[colorName];
}

function getIconComponent(iconName: string | null | undefined): LucideIcon {
  if (!iconName) return Package;
  return ICON_MAP[iconName] || Package;
}

function getColorStyles(colorName: string | null | undefined, isSelected: boolean): string {
  if (!colorName || !COLOR_STYLES[colorName]) {
    return isSelected
      ? "bg-primary text-primary-foreground hover:bg-primary/90"
      : "bg-background border-input hover:bg-accent hover:text-accent-foreground";
  }
  return isSelected ? COLOR_STYLES[colorName].selected : COLOR_STYLES[colorName].unselected;
}

function getPdvColorStyles(colorName: string | null | undefined, isSelected: boolean): string {
  if (!colorName || !PDV_COLOR_STYLES[colorName]) {
    // Default PDV styles
    return isSelected
      ? "bg-[#0a3a6e] border-[#0077cc] text-[#4dd0ff] shadow-[0_0_10px_rgba(0,170,255,0.2)]"
      : "bg-[#0a0e1a] border-[#1e2d52] text-[#4a7abf] hover:bg-[#142040] hover:border-[#1a5fa0]";
  }
  return isSelected ? PDV_COLOR_STYLES[colorName].selected : PDV_COLOR_STYLES[colorName].unselected;
}

interface CategoryTabsProps {
  categories: Category[];
  selectedCategory: string | null;
  onSelectCategory: (categoryId: string | null) => void;
}

export function CategoryTabs({
  categories,
  selectedCategory,
  onSelectCategory,
}: CategoryTabsProps) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#3a5a8c] font-semibold mb-1.5 px-0.5">Categorias</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-1.5">
        {/* "Todos" tile — discreto/invisível */}
        <button
          onClick={() => onSelectCategory(null)}
          className={cn(
            "flex flex-col items-center justify-center min-h-[44px] rounded-[10px] border p-2 transition-all duration-150 cursor-pointer",
            selectedCategory === null
              ? "bg-[#111829] border-[#1e2d52] text-[#7aabdd]"
              : "bg-transparent border-[#1e2d52]/50 text-[#3a5a8c] hover:bg-[#111829]/50 hover:text-[#4a7abf]"
          )}
        >
          <Package className="h-4 w-4 mb-0.5 opacity-60" />
          <span className="text-[9px] font-semibold text-center leading-tight">Todos</span>
        </button>
        {categories.map((category) => {
          const isSelected = selectedCategory === category.id;
          const IconComponent = getIconComponent(category.icon);

          return (
            <button
              key={category.id}
              onClick={() => onSelectCategory(category.id)}
              className={cn(
                "flex flex-col items-center justify-center min-h-[44px] rounded-[10px] border p-2 transition-all duration-150 cursor-pointer",
                isSelected && "scale-[1.03]",
                getPdvColorStyles(category.color, isSelected)
              )}
            >
              <IconComponent className="h-5 w-5 mb-0.5" />
              <span className="text-[10px] font-bold text-center leading-tight line-clamp-1">{category.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
